import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
  Chip,
  Pagination,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';

import specService, { SpecItem } from '../services/specification.service';
import { ConfirmDialog } from '../components/common';
import {
  getSpecMeta,
  SPECIFICATION_TYPES,
  ACTIVE_SPECIFICATION_TYPES,
  SPECIFICATION_GROUPS,
  SpecificationGroup,
  SpecificationTypeMeta,
} from '../constants/specification-types';

/** Active (cosmetics) spec types bucketed by group, in group order. */
const ACTIVE_GROUPS: { group: SpecificationGroup; labelFa: string; types: SpecificationTypeMeta[] }[] =
  (Object.keys(SPECIFICATION_GROUPS) as SpecificationGroup[])
    .filter((g) => g !== 'legacy')
    .sort((a, b) => SPECIFICATION_GROUPS[a].order - SPECIFICATION_GROUPS[b].order)
    .map((g) => ({
      group: g,
      labelFa: SPECIFICATION_GROUPS[g].labelFa,
      types: ACTIVE_SPECIFICATION_TYPES.filter((t) => t.group === g),
    }))
    .filter((g) => g.types.length > 0);

const LEGACY_TYPES: SpecificationTypeMeta[] = SPECIFICATION_TYPES.filter((t) => t.legacy);

const SpecTypeChips: React.FC<{ types: SpecificationTypeMeta[]; current: string }> = ({
  types,
  current,
}) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
    {types.map((t) => (
      <Chip
        key={t.type}
        size="small"
        component={RouterLink}
        to={`/specifications/${t.type}`}
        clickable
        label={t.labelFa}
        color={t.type === current ? 'primary' : 'default'}
        variant={t.type === current ? 'filled' : 'outlined'}
      />
    ))}
  </Box>
);

/** Guide listing every active spec type by group; legacy types tucked away. */
const SpecTypeGuide: React.FC<{ current: string }> = ({ current }) => {
  const currentIsLegacy = LEGACY_TYPES.some((t) => t.type === current);
  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {ACTIVE_GROUPS.map((g) => (
        <Box
          key={g.group}
          sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
        >
          <Typography
            sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#4A2A3A', minWidth: 150, pt: 0.25 }}
          >
            {g.labelFa}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SpecTypeChips types={g.types} current={current} />
          </Box>
        </Box>
      ))}
      {LEGACY_TYPES.length > 0 && (
        <Accordion
          key={currentIsLegacy ? 'legacy-open' : 'legacy-closed'}
          defaultExpanded={currentIsLegacy}
          disableGutters
          elevation={0}
          sx={{ border: '1px dashed #e2e8f0', borderRadius: 1, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              قدیمی — خارج از کاتالوگ آرایشی‌بهداشتی ({LEGACY_TYPES.length.toLocaleString('fa-IR')})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SpecTypeChips types={LEGACY_TYPES} current={current} />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

interface FormState {
  nameFa: string;
  nameEn: string;
  slug: string;
  description: string;
  parent: string | null;
  sortOrder: number;
  isActive: boolean;
}

const emptyForm: FormState = {
  nameFa: '',
  nameEn: '',
  slug: '',
  description: '',
  parent: null,
  sortOrder: 0,
  isActive: true,
};

const SpecificationManagement: React.FC = () => {
  const { type = '' } = useParams<{ type: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const meta = getSpecMeta(type);

  const [items, setItems] = useState<SpecItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Parent type options (for hierarchical specs)
  const [parentOptions, setParentOptions] = useState<SpecItem[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SpecItem | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<SpecItem | null>(null);

  // Reset everything when route type changes
  useEffect(() => {
    setItems([]);
    setTotal(0);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setShowInactive(false);
    setParentOptions([]);
  }, [type]);

  const load = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    try {
      const res = await specService.list({
        type,
        search: search || undefined,
        activeOnly: !showInactive,
        page,
        limit,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      enqueueSnackbar('خطا در دریافت لیست', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [type, search, page, limit, showInactive, meta, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // When the active type has a parent type, load all parent options once.
  useEffect(() => {
    if (!meta?.parentType) {
      setParentOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await specService.list({
          type: meta.parentType!,
          activeOnly: true,
          limit: 500,
        });
        setParentOptions(res.items);
      } catch {
        /* parent options are optional */
      }
    })();
  }, [meta]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, sortOrder: items.length + 1 });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (item: SpecItem) => {
    setEditing(item);
    const parentId =
      item.parent && typeof item.parent === 'object'
        ? item.parent._id
        : (item.parent as string | null) || null;
    setForm({
      nameFa: item.nameFa,
      nameEn: item.nameEn || '',
      slug: item.slug,
      description: item.description || '',
      parent: parentId,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!form.nameFa.trim()) {
      setFormError('نام فارسی الزامی است');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        type,
        nameFa: form.nameFa.trim(),
        nameEn: form.nameEn.trim() || undefined,
        slug: form.slug.trim() || undefined,
        description: form.description.trim() || undefined,
        parent: form.parent,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      };
      if (editing) {
        await specService.update(type, editing._id, payload);
        enqueueSnackbar('بروزرسانی شد', { variant: 'success' });
      } else {
        await specService.create(payload);
        enqueueSnackbar('ایجاد شد', { variant: 'success' });
      }
      closeDialog();
      await load();
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await specService.remove(type, deleteTarget._id);
      enqueueSnackbar('حذف شد', { variant: 'success' });
      setDeleteTarget(null);
      await load();
    } catch {
      enqueueSnackbar('خطا در حذف', { variant: 'error' });
    }
  };

  const handleToggle = async (item: SpecItem) => {
    try {
      await specService.toggle(type, item._id);
      await load();
    } catch {
      enqueueSnackbar('خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const handleRestore = async (item: SpecItem) => {
    try {
      await specService.restore(type, item._id);
      enqueueSnackbar('بازیابی شد', { variant: 'success' });
      await load();
    } catch {
      enqueueSnackbar('خطا در بازیابی', { variant: 'error' });
    }
  };

  if (!meta) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          نوع مشخصه‌ای با شناسه «{type}» تعریف نشده. لطفاً یکی از انواع زیر را انتخاب کنید.
        </Alert>
        <SpecTypeGuide current={type} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {meta.labelFa}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
            collection: <code>{meta.collectionName}</code> • model:{' '}
            <code>{meta.modelName}</code>
            {meta.parentType && (
              <>
                {' '}
                • وابسته به <code>{getSpecMeta(meta.parentType)?.labelFa}</code>
              </>
            )}
          </Typography>
          {meta.hint && (
            <Typography sx={{ fontSize: '0.8rem', color: '#b45309', mt: 0.5 }}>
              {meta.hint}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            بروزرسانی
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            افزودن
          </Button>
        </Stack>
      </Box>

      <SpecTypeGuide current={type} />

      <Box
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}
      >
        <TextField
          size="small"
          placeholder="جستجو نام/slug..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button type="submit" variant="outlined" size="small">
          جستجو
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Switch
            size="small"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <Typography sx={{ fontSize: '0.85rem', ml: 0.5 }}>
            نمایش غیرفعال‌ها / حذف‌شده‌ها
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center', color: '#94a3b8' }}>
            <Typography>هیچ آیتمی یافت نشد.</Typography>
          </Box>
        ) : (
          items.map((item) => {
            const parentLabel =
              item.parent && typeof item.parent === 'object'
                ? item.parent.nameFa
                : null;
            return (
              <Box
                key={item._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderBottom: '1px solid #f1f5f9',
                  opacity: item.isActive ? 1 : 0.55,
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: '#fafbfc' },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: item.isActive ? '#FFF4F7' : '#f1f5f9',
                    color: item.isActive ? '#B25373' : '#94a3b8',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}
                >
                  {item.sortOrder}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {item.nameFa}
                    {item.nameEn && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.75rem',
                          color: '#64748b',
                          ml: 1,
                          fontWeight: 400,
                        }}
                      >
                        ({item.nameEn})
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    <code>{item.slug}</code>
                    {parentLabel && <> • زیرمجموعه‌ی <strong>{parentLabel}</strong></>}
                    {item.isDeleted && (
                      <Chip
                        label="حذف‌شده"
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                      />
                    )}
                  </Typography>
                </Box>
                <Tooltip title={item.isActive ? 'فعال' : 'غیرفعال'}>
                  <Switch
                    size="small"
                    checked={item.isActive}
                    onChange={() => handleToggle(item)}
                    disabled={item.isDeleted}
                  />
                </Tooltip>
                {item.isDeleted ? (
                  <Tooltip title="بازیابی">
                    <IconButton size="small" onClick={() => handleRestore(item)}>
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title="ویرایش">
                      <IconButton size="small" onClick={() => openEdit(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="حذف">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(item)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {total > limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? `ویرایش «${editing.nameFa}»` : `افزودن ${meta.labelFa}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError && (
            <Box
              sx={{
                bgcolor: '#fee2e2',
                color: '#991b1b',
                p: 1,
                borderRadius: 1,
                fontSize: '0.85rem',
              }}
            >
              {formError}
            </Box>
          )}
          <TextField
            label="نام فارسی"
            value={form.nameFa}
            onChange={(e) => setForm({ ...form, nameFa: e.target.value })}
            required
            autoFocus
            fullWidth
          />
          <TextField
            label="نام انگلیسی"
            value={form.nameEn}
            onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
            fullWidth
          />
          <TextField
            label="Slug (اختیاری — خودکار از نام انگلیسی)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            fullWidth
            placeholder="مثلاً: la-roche-posay"
          />
          {meta.parentType && (
            <Autocomplete
              options={parentOptions}
              value={
                parentOptions.find((p) => p._id === form.parent) || null
              }
              onChange={(_, value) =>
                setForm({ ...form, parent: value?._id || null })
              }
              getOptionLabel={(o) => o.nameFa}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`والد (${getSpecMeta(meta.parentType!)?.labelFa})`}
                />
              )}
            />
          )}
          <TextField
            label="توضیحات"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="ترتیب نمایش"
              type="number"
              value={form.sortOrder}
              onChange={(e) =>
                setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })
              }
              sx={{ width: 140 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <Typography>{form.isActive ? 'فعال' : 'غیرفعال'}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            انصراف
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`حذف «${deleteTarget?.nameFa}»`}
        message={'مطمئنید؟ اگر این آیتم وابستگی‌هایی دارد (مثل آیتم‌های زیرمجموعهٔ آن)، آن‌ها نیز حذف می‌شوند.'}
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

export default SpecificationManagement;
